/**
 *
 * Component: VisualConfigCard
 * Date: 14/7/2020
 *
 */

import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDrag, useDrop } from 'react-dnd';

import { API } from 'utils/request';
import {
  Input,
  Card,
  Text,
  Switch,
  Modal,
  Avatar,
  Icon,
  FileUploader,
} from 'components/common';
import { getBaseUrl } from 'utils/commonFunctions';

import { Spin } from 'antd';
import style from './style.module.css';

/**
 * Exceptional Component making an API calls.
 */
const VisualConfigCard = ({
  handleDrop,
  visual,
  visualIndex,
  handleRefChange,
  deleteVisual,
  acceptDropList,
  fetchList,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
any) => {
  const [visualState, setVisualState] = useState(visual);
  const [loading, setLoading] = useState(false);
  const [visibleConfirm, setVisibleConfirm] = useState(false);

  const [idError, setIdError] = useState('');
  const [nameError, setNameError] = useState('');
  const [URLError, setURLError] = useState('');

  const [dragResponse, drag] = useDrag({
    type: 'CARD',
    item: { type: visualState.id, index: visualIndex },
    collect: (monitor: any) => ({
      isDragging: !!monitor.isDragging(),
    }),
  } as any);
  const { isDragging } = dragResponse as any;

  const [{ isOver }, drop] = useDrop({
    accept: acceptDropList,
    drop: (item: any) => {
      handleDrop(item.index, visualIndex);
    },
    collect: (mon: any) => ({
      isOver: !!mon.isOver(),
      canDrop: !!mon.canDrop(),
    }),
  });

  /**
   * check and validate for errors, if any error update the respective field
   * @param e : element object,
   * @param type : string of update to make
   */
  const checkErrors = (e: any, type: string) => {
    switch (type) {
      case 'id':
        if (!e.target.value) setIdError('Id cannot be empty');
        else if (idError) setIdError('');
        break;
      case 'name':
        if (!e.target.value) setNameError('Name cannot be empty');
        else if (nameError) setNameError('');
        break;
      case 'url':
        if (!e.target.value) setURLError('URL cannot be empty');
        else if (URLError) setURLError('');
        break;
      default:
        break;
    }
  };

  /**
   * update input values, based on element ref provided
   * @param e
   * @param type : string for respective update
   */
  const handleInputChange = (e: any, type: string) => {
    checkErrors(e, type);
    handleRefChange(e, type);
  };

  /**
   * Gets Base64 after setting FileReaderAPI.
   * @param {*} img : Image data.
   * @param {*} callback : Callback to use results, after read.
   */
  function getBase64(img: any, callback: any, errCb?: any) {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.addEventListener('error', errCb);
    reader.readAsDataURL(img);
  }

  /**
   * Sets the loading indicator for file upload else, on completion, gets the Base64.
   * @param {Object} info : FileUpload Info:
   * @returns nothing.
   */
  const handleChange = (info: any) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      /**
       * Get this url from response in real world.
       * */
      getBase64(
        info.file.originFileObj,
        (imageUrl: string) => {
          setLoading(false);
          const newVisualState = _.cloneDeep(visualState);
          /**
           * to display image on upload without getting it from S3 bucket
           * */
          newVisualState.thumbnailUrl = imageUrl;
          setVisualState(newVisualState);
          fetchList();
        },
        () => {
          setLoading(false);
        },
      );
    }
  };

  useEffect(() => {
    ['id', 'name', 'url'].forEach((key: string) => {
      checkErrors({ target: { value: visual[key] || '' } }, key);
    });
    setVisualState(visual);
  }, [visual]);

  const uploadButton = (
    <div className={style.uploadButton}>
      <Icon size={30} type={loading ? 'loading' : 'plus'} />
      <div className="ant-upload-text">Upload</div>
    </div>
  );

  return (
    <div key={visualState.id} className={isDragging ? style.isDragging : ''}>
      <div ref={drop} className={isOver ? style.isOver : ''}>
        <Card className={style.visualConfigCard}>
          <div className={style.deleteIcon}>
            <Icon
              type="delete"
              onClick={
                () => visual.id && setVisibleConfirm(visual.id)
                // eslint-disable-next-line react/jsx-curly-newline
              }
            />
          </div>
          <div ref={drag} className={style.isDraggable} />
          <div className={style.visualConfigThumbnail}>
            <FileUploader
              acceptType="png,jpg,jpeg"
              uploadButton={
                // eslint-disable-next-line no-nested-ternary
                loading ? (
                  <Spin wrapperClassName={style.spinerStyles} />
                ) : visualState.thumbnailUrl ? (
                  <Avatar
                    size={280}
                    shape="square"
                    src={visualState.thumbnailUrl}
                  />
                ) : (
                  uploadButton
                )
              }
              name="avatar"
              listType="picture-card"
              showUploadList={false}
              action={`${getBaseUrl(true)}${API.uploadThumbnail}?thumbnail_id=${
                visualState.id
              }&config_id=${window.location.pathname.replace(
                '/configs/',
                '',
              )}&token=${`Bearer ${localStorage.getItem('access_token')}`}`}
              onChange={handleChange}
            />
          </div>
          <div className={style.visualDetails}>
            <div>
              <Text text="ID" type="label" />
              <Input
                type="input"
                onChange={(e: any) => handleInputChange(e, 'id')}
                defaultValue={visualState.id}
              />
              {!!idError && <Text type="error" text={idError} />}
            </div>
            <div className={style.switchDiv}>
              <Text text="Public" type="label" />
              <Switch
                size="small"
                onChange={
                  (e: any) =>
                    handleInputChange({ target: { value: e } }, 'isPublic')
                  // eslint-disable-next-line react/jsx-curly-newline
                }
                defaultChecked={visualState.isPublic}
              />
            </div>
            <div>
              <Text text="Name" type="label" />
              <Input
                type="input"
                onChange={(e: any) => handleInputChange(e, 'name')}
                defaultValue={visualState.name}
              />
              {!!nameError && <Text type="error" text={nameError} />}
            </div>
            <div>
              <Text text="Tableau URL" type="label" />
              <Input
                type="input"
                onChange={(e: any) => handleInputChange(e, 'url')}
                defaultValue={visualState.url}
              />
              {!!URLError && <Text type="error" text={URLError} />}
            </div>
          </div>
        </Card>
      </div>
      <Modal
        title={`confirm delete ${visibleConfirm}?`}
        visible={visibleConfirm}
        onAccept={() => {
          deleteVisual(visibleConfirm);
          setVisibleConfirm(false);
        }}
        onCancel={() => setVisibleConfirm(false)}
        footer
        acceptButtonText="Confirm"
        width="500px"
      />
    </div>
  );
};

export default VisualConfigCard;
